import { Inject } from '@odinjs/odin';
import { FormAPI, ModalService, ThemeTonalRange, ToastService } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import Controller, { OnBeforeCancel, OnClose, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';

import { ApplicationRoute, ModulesRoute } from '../../constants';
import { Endpoint } from '../../constants/endpoints';
import showSuccessToast from '../../utils/show-toast';
import { approvalDisapproveModalID } from '../approvals/approvals-item-controller';
import BaseController from '../BaseController';
import { DialogType, ModalType, SimpleDialog, SimpleDialogParams } from '../dialog-helper/dialog-helper';

import { normalizeDisapproveBody } from './approval-disapprove-business';

export interface Form {
  reason: Reason
  observation: string
}

interface Reason {
  id: number | string,
  value: string
}

@Controller({ fragment: 'disapprove-confirm-modal', component: 'disapprove-layout' })
export default class ApprovalDisapproveModalController extends BaseController {

  @Inject('disapprove-form')
    disapproveformApi: FormAPI;

  @OnBeforeCancel({ target: 'disapprove-form' })
  onFormBeforeCancel(): void {
    const record = this.disapproveformApi.getRecord();
    this.disapproveformApi.setRecord(record);
    this.disapproveformApi.setDisabled(true);

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'approval.discard.changes',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => { this.cancelDiscard();},
      onClickOk: () => { this.discard();},
      titleDialog: 'approvals.action.disapprove',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'approval.discard',
      theme: ThemeTonalRange.VERY_DARK,
    };
    SimpleDialog.openDialog(dialogParams);
  }

  @OnSubmit('disapprove-form')
  onFormSubmit(event: SubmitEvent): void {
    const form = event.target as FormAPI;
    const { id, processId } = this.historyService.getCurrentRouteParams() as Record<string, undefined>;
    const formData = event.record as unknown as Form;

    form.setLoading(true);
    const body = normalizeDisapproveBody(formData, id, processId);

    HttpService.post(Endpoint.DISAPPROVE_PENDING_PROCESS, {}, { body })
      .then(() => this.successDisapprove())
      .catch(error => this.handlerError(error))
      .finally(() => this.completeSubmit(form));
  }

  @OnClose(['disapprove-confirm-modal', 'approval-disapprove-modal'])
  onCloseModal(): void {
    ModalService.close(approvalDisapproveModalID);
  }

  successDisapprove(): void {
    showSuccessToast('approval-disapprove-complete-toast', 'pending.approval.action.completed.successfully');
    ModalService.close(approvalDisapproveModalID);
  }

  handlerError(error: any): void {
    this.context.set({ hasErrors: true, error });
    let errorMessage = 'Error.';
    if (error && error.body && error.body.errors) {
      errorMessage = error.body.errors.toString();
    }
    ToastService.open({
      id: 'approval-disapprove-toast',
      dismissible: false,
      message: errorMessage,
      level: ToastLevel.ERROR,
      timeout: 5000,
    });
  }

  completeSubmit(form: FormAPI): void {
    const hasErrorrs = this.context.get('hasErrors') as boolean | undefined;
    form.setLoading(false);
    if (!hasErrorrs) {
      applicationContext.redirect(`${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}`);
    }

    this.context.set({ hasErrorrs: false, error: undefined });
  }

  cancelDiscard(): void {
    this.disapproveformApi.setDisabled(false);
    this.disapproveformApi.setEditing(true);
  }

  discard(): void {
    applicationContext.redirect(
      `${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}`
    );
  }
}
